import { QuestionnaireResponse } from '@medplum/fhirtypes';
import { cloneDeep, isEqual } from 'lodash';

/**
 * Creates a deep copy of a questionnaire response
 * @param response - The questionnaire response to clone
 * @returns A deep copy of the response, or undefined if input is undefined
 */
export function cloneQuestionnaireResponse(
  response: QuestionnaireResponse | undefined
): QuestionnaireResponse | undefined {
  if (!response) {
    return undefined;
  }

  return cloneDeep(response);
}

//------------------------------------------------------------------------------
export function flattenAnswers(
  items: any[] | undefined,
  result: Record<string, any> = {}
): Record<string, any> {
  if (!items) {
    return result;
  }

  for (const item of items) {
    const answers = item.answer;

    if (answers && answers.length > 0) {
      const first = answers[0];

      // Is it a “blank” answer? ({}, {valueString: ""}, etc.)
      const hasValue =
        (first.valueString !== undefined && first.valueString !== '') ||
        first.valueInteger !== undefined ||
        first.valueBoolean !== undefined ||
        first.valueCoding?.code ||
        first.valueAttachment ||
        first.valueQuantity ||
        false;

      if (hasValue) {
        const normalized =
          first.valueString ??
          first.valueInteger ??
          first.valueBoolean ??
          first.valueCoding?.code ??
          null;

        result[item.linkId] = normalized;
      }
      // if no value -> ignore
    }

    if (item.item) {
      flattenAnswers(item.item, result);
    }
  }

  return result;
}

export function areQuestionnaireResponsesEqual(
  a?: QuestionnaireResponse,
  b?: QuestionnaireResponse
): boolean {
  if (!a && !b) {
    return true;
  }
  if (!a || !b) {
    return false;
  }

  const fa = flattenAnswers(a.item);
  const fb = flattenAnswers(b.item);

  return isEqual(fa, fb);
}
